import React, { useState, useEffect, useContext, useCallback, useRef, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import BarChartIcon from "@mui/icons-material/BarChart";
import { StyledButton } from "../../components/styled-button/styled-button";

import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
} from "recharts";
import { useTranslation } from "react-i18next";
import { ApplicationsContext } from "./applications-context";
import ChooseDatesReportComponent from "./chooseDates/choose-dates";
import { useService } from "./applications-service";
import { AppConfig } from "../../context/context";
import "./application-usage.scss";

interface IUsageViewProps {
  app: {
    clientId: string;
    name: string;
  };
  onBack: () => void;
}

interface IMetric {
  name: string;
  callCountTotal: number;
  callCountSuccess: number;
  callCountError: number;
  callCountBlocked: number;
}

interface IChartTipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
}

// Helper Functions
const formatWithOffset = (date: Date, isEnd: boolean) => {
  const d = new Date(date);

  if (isEnd) {
    d.setHours(23, 59, 59, 999);
  } else {
    d.setHours(0, 0, 0, 0);
  }

  const pad = (n: number) => n.toString().padStart(2, "0");

  const t = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;

  const offsetMinutes = d.getTimezoneOffset();
  const absOffset = Math.abs(offsetMinutes);
  const offsetHours = pad(Math.floor(absOffset / 60));
  const offsetMins = pad(absOffset % 60);
  const sign = offsetMinutes > 0 ? "-" : "+";

  return `${t}${sign}${offsetHours}:${offsetMins}`;
};

const ChartTip: React.FC<IChartTipProps> = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Paper className="usage-chart-tooltip">
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          {label}
        </Typography>

        {payload.map((p) => (
          <Box key={p.name} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "3px",
                backgroundColor: p.color,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {p.name}: <strong>{p.value.toLocaleString()}</strong>
            </Typography>
          </Box>
        ))}
      </Paper>
    );
  }

  return null;
};

const ApplicationUsage: React.FC<IUsageViewProps> = ({ app, onBack }) => {
  const { t } = useTranslation();
  const { appService } = useService();
  const { selectedCheckedApps, applications } = useContext(ApplicationsContext);
  const { config } = useContext(AppConfig);

  const [metrics, setMetrics] = useState<IMetric[]>([]);
  const [loading, setLoading] = useState(false);
  const [apiReturnedNull, setApiReturnedNull] = useState(false);
  const [selectedAppNames, setSelectedAppNames] = useState<string[]>([]);

  const [startDate, setStartDate] = useState<Date | null>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d;
  });

  const [endDate, setEndDate] = useState<Date | null>(new Date());

  const containerRef = useRef<HTMLDivElement>(null);

  // Store last valid selection to revert if user leaves selection blank or incomplete
  const lastValidDates = useRef<[Date, Date]>([startDate!, endDate!]);

  // Flag to prevent redundant API calls when reverting dates
  const skipNextFetch = useRef(false);

  const finalizeDateSelection = useCallback(() => {
    if (!startDate && !endDate) {
      skipNextFetch.current = true;
      setStartDate(lastValidDates.current[0]);
      setEndDate(lastValidDates.current[1]);
    } else if (startDate && !endDate) {
      // If only start date is selected, automatically set a 1-day range for that date
      setEndDate(startDate);
      lastValidDates.current = [startDate, startDate];
    } else if (!startDate && endDate) {
      // If only end date is selected, automatically set a 1-day range for that date
      setStartDate(endDate);
      lastValidDates.current = [endDate, endDate];
    }
  }, [startDate, endDate]);

  const handleCalendarBlur = (e: React.FocusEvent) => {
    // Detect when focus leaves the entire calendar selection Box
    if (!e.currentTarget.contains(e.relatedTarget as any)) {
      finalizeDateSelection();
    }
  };

  // Handle global click-outside to catch scenarios where focus wasn't captured correctly
  useEffect(() => {
    const handleGlobalClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      if (containerRef.current && !containerRef.current.contains(target)) {
        const isPortalClick = !!(
          target.closest(".react-datepicker-popper") ||
          target.closest(".MuiPopover-root") ||
          target.closest(".MuiDialog-root") ||
          target.closest('[role="presentation"]')
        );

        if (!isPortalClick) {
          finalizeDateSelection();
        }
      }
    };

    document.addEventListener("mousedown", handleGlobalClick);

    return () => document.removeEventListener("mousedown", handleGlobalClick);
  }, [finalizeDateSelection]);

  useEffect(() => {
    if (selectedCheckedApps.length > 0 && applications?.devTenantApps) {
      const names = selectedCheckedApps.map((appId) => {
        const foundApp = applications.devTenantApps.find((a) => a.id === appId);
        return foundApp ? foundApp.name : "";
      });

      setSelectedAppNames(names);
    } else {
      setSelectedAppNames([]);
    }
  }, [selectedCheckedApps, applications]);

  const handleFetchMetrics = useCallback(async () => {
    if (!config?.tenantId || !selectedAppNames[0]) return;
    if (!startDate || !endDate) return;

    if (skipNextFetch.current) {
      skipNextFetch.current = false;
      return;
    }

    const startParam = encodeURIComponent(formatWithOffset(startDate, false));
    const endParam = encodeURIComponent(formatWithOffset(endDate, true));

    setLoading(true);
    setApiReturnedNull(false);

    try {
      const response = await appService.getUsageMetrics(
        config.tenantId,
        selectedAppNames[0],
        startParam,
        endParam
      );

      if (!Array.isArray(response) || response.length === 0) {
        setMetrics([]);
        setApiReturnedNull(true);
      } else {
        setMetrics(response);
      }
    } catch (error) {
      console.error("Failed to fetch metrics", error);
      setMetrics([]);
      setApiReturnedNull(true);
    } finally {
      setLoading(false);
    }
  }, [selectedAppNames, config?.tenantId, startDate, endDate, appService]);

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    setStartDate(dates[0]);
    setEndDate(dates[1]);

    if (dates[0] && dates[1]) {
      lastValidDates.current = [dates[0], dates[1]];
      setLoading(true);
      setApiReturnedNull(false);
    }
  };

  useEffect(() => {
    handleFetchMetrics();
  }, [handleFetchMetrics]);

  const totalStats = useMemo(() => {
    const stats = metrics.reduce(
      (acc, curr) => ({
        total: acc.total + curr.callCountTotal,
        success: acc.success + curr.callCountSuccess,
        error: acc.error + curr.callCountError,
        blocked: acc.blocked + curr.callCountBlocked,
      }),
      { total: 0, success: 0, error: 0, blocked: 0 }
    );

    return {
      ...stats,
      successPct: stats.total > 0 ? ((stats.success / stats.total) * 100).toFixed(1) : "0.0",
      errorPct: stats.total > 0 ? ((stats.error / stats.total) * 100).toFixed(1) : "0.0",
      blockedPct: stats.total > 0 ? ((stats.blocked / stats.total) * 100).toFixed(1) : "0.0",
    };
  }, [metrics]);

  const barData = useMemo(
    () =>
      metrics
        .filter((a) => a.callCountTotal > 0)
        .map((a) => ({
          name: a.name.replace(" Service API", "").replace(" API", ""),
          Success: a.callCountSuccess,
          Error: a.callCountError,
          Blocked: a.callCountBlocked,
          Total: a.callCountTotal,
        })),
    [metrics]
  );

  return (
    <Box className="usage-container-root">
      <Paper className="usage-main-paper" elevation={2}>
        <Box className="usage-header-section">
          <Button
            onClick={onBack}
            className="back-btn-link"
            startIcon={<ArrowBackIcon fontSize="small" />}
          >
            {t("back_to_apps")}
          </Button>

          <Typography variant="h5" className="usage-page-title">
            {t("api_usage_for_app", { appName: app.name })}
          </Typography>

          <Box
            ref={containerRef}
            className="usage-controls-row"
            onBlur={handleCalendarBlur}
          >
            <ChooseDatesReportComponent
              startDate={startDate}
              endDate={endDate}
              onDateChange={handleDateChange}
            />

            <Button
              variant="outlined"
              size="small"
              className="usage-refresh-btn"
              onClick={handleFetchMetrics}
              disabled={loading}
              startIcon={<RefreshIcon />}
            >
              {loading ? t("refreshing") : t("refresh")}
            </Button>
          </Box>
        </Box>

        <Box className="usage-content-divider">
          {loading ? (
            <Box
              sx={{
                minHeight: 360,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress size={32} aria-label={t("loading_data_usage")} />
            </Box>
          ) : apiReturnedNull ? (
            <Box className="usage-empty-chart">
              <Typography className="empty-msg">{t("no_data_usage")}</Typography>
              <Typography className="empty-msg">{t("select_different_date")}</Typography>
            </Box>
          ) : (
            <>
              <Box className="usage-summary-grid">
                {[
                  {
                    label: t("total"),
                    value: totalStats.total.toLocaleString(),
                    color: "var(--color-active-btn)",
                    bg: "var(--bg-total)",
                  },
                  {
                    label: t("success"),
                    value: totalStats.success.toLocaleString(),
                    color: "var(--color-success)",
                    bg: "var(--bg-success)",
                    sub: `${totalStats.successPct}%`,
                  },
                  {
                    label: t("error"),
                    value: totalStats.error.toLocaleString(),
                    color: "var(--color-error)",
                    bg: "var(--bg-error)",
                    sub: `${totalStats.errorPct}%`,
                  },
                  {
                    label: t("blocked"),
                    value: totalStats.blocked.toLocaleString(),
                    color: "var(--color-text-sub)",
                    bg: "var(--bg-blocked)",
                    sub: `${totalStats.blockedPct}%`,
                  },
                ].map((s) => (
                  <Box
                    key={s.label}
                    className="summary-stat-card"
                    style={{ background: s.bg }}
                  >
                    <Typography className="summary-label">{s.label}</Typography>

                    <Typography className="summary-value" style={{ color: s.color }}>
                      {s.value}
                    </Typography>

                    {s.sub && <Typography className="summary-sub">{s.sub}</Typography>}
                  </Box>
                ))}
              </Box>

              <Box className="usage-charts-grid">
                <Box className="usage-chart-paper">
                  <Typography className="chart-header-text">
                    {t("total_vol_by_api")}
                  </Typography>

                  {totalStats.total === 0 && !loading ? (
                    <Box className="usage-empty-chart">
                      <Typography className="empty-msg">{t("no_data_usage")}</Typography>
                      <Typography className="empty-msg">
                        {t("select_different_date")}
                      </Typography>
                    </Box>
                  ) : (
                    <ResponsiveContainer width="100%" height={loading ? 0 : 180}>
                      <BarChart data={barData}>
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke="var(--color-border)"
                          vertical={false}
                        /> 
                        <XAxis dataKey="name" axisLine={false} tickLine={false} />
                        <YAxis axisLine={false} tickLine={false} allowDecimals={false} />
                        <Tooltip content={<ChartTip />} />
                        <Bar
                          dataKey="Total"
                          fill="var(--color-active-btn)"
                          radius={[3, 3, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Box>

                <Box className="usage-chart-paper">
                  <Typography className="chart-header-text">
                    {t("requests_by_api")}
                  </Typography>

                  {totalStats.total === 0 && !loading ? (
                    <Box className="usage-empty-chart">
                      <Typography className="empty-msg">{t("no_data_usage")}</Typography>
                      <Typography className="empty-msg">
                        {t("select_different_date")}
                      </Typography>
                    </Box>
                  ) : (
                    <ResponsiveContainer width="100%" height={loading ? 0 : 180}>
                      <BarChart
                        data={barData}
                        barGap={3}
                        barCategoryGap="30%"
                        margin={{ top: 10, right: 10, bottom: 0, left: 0 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke="var(--color-border)"
                          vertical={false}
                        />
                        <XAxis dataKey="name" axisLine={false} tickLine={false} />
                        <YAxis
                          axisLine={false}
                          tickLine={false}
                          allowDecimals={false}
                          domain={[0, (dataMax: number) => Math.ceil(dataMax * 1.25)]}
                        />
                        <Tooltip content={<ChartTip />} />
                        <Legend
                          iconType="rect"
                          wrapperStyle={{ fontSize: 11, paddingTop: 6 }}
                          content={() => (
                            <div
                              style={{
                                display: "flex",
                                gap: 12,
                                justifyContent: "center",
                                fontSize: 11,
                              }}
                            >
                              {[
                                { label: "Success", color: "var(--color-success)" },
                                { label: "Error", color: "var(--color-error)" },
                                { label: "Blocked", color: "var(--color-blocked)" },
                              ].map(({ label, color }) => (
                                <div
                                  key={label}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                  }}
                                >
                                  <div
                                    style={{
                                      width: 12,
                                      height: 12,
                                      background: color,
                                      borderRadius: 2,
                                    }}
                                  />
                                  <Box component="span">{label}</Box>
                                </div>
                              ))}
                            </div>
                          )}
                        />
                        <Bar
                          dataKey="Success"
                          fill="var(--color-success)"
                          radius={[3, 3, 0, 0]}
                        />
                        <Bar
                          dataKey="Error"
                          fill="var(--color-error)"
                          radius={[3, 3, 0, 0]}
                        />
                        <Bar
                          dataKey="Blocked"
                          fill="var(--color-blocked)"
                          radius={[3, 3, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Box>
              </Box>

              <TableContainer component={Box} className="usage-table-container">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {[
                        t("api_name"),
                        t("success"),
                        t("error"),
                        t("blocked"),
                        t("total"),
                        t("blocked_percent"),
                      ].map((h, idx) => (
                        <TableCell
                          key={h}
                          className={`usage-table-header-cell ${
                            idx !== 0 ? "align-right" : ""
                          }`}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {metrics.map((api, i) => {
                      const bp =
                        api.callCountTotal > 0
                          ? ((api.callCountBlocked / api.callCountTotal) * 100).toFixed(1)
                          : "0.0";

                      const bHigh = parseFloat(bp) >= 10;
                      const bMid = parseFloat(bp) >= 5 && !bHigh;

                      return (
                        <TableRow key={i} className="usage-table-row">
                          <TableCell className="usage-table-cell">{api.name}</TableCell>

                          <TableCell className="usage-table-cell align-right success">
                            {api.callCountSuccess.toLocaleString()}
                          </TableCell>

                          <TableCell className="usage-table-cell align-right">
                            {api.callCountError > 0 ? (
                              <Box component="span" className="error">
                                {api.callCountError.toLocaleString()}
                              </Box>
                            ) : (
                              <Box component="span" className="muted-dash">
                                —
                              </Box>
                            )}
                          </TableCell>

                          <TableCell className="usage-table-cell align-right">
                            {api.callCountBlocked > 0 ? (
                              <Box component="span" className="blocked">
                                {api.callCountBlocked.toLocaleString()}
                              </Box>
                            ) : (
                              <Box component="span" className="muted-dash">
                                —
                              </Box>
                            )}
                          </TableCell>

                          <TableCell className="usage-table-cell align-right total">
                            {api.callCountTotal.toLocaleString()}
                          </TableCell>

                          <TableCell className="usage-table-cell align-right">
                            <Box
                              component="span"
                              className={`usage-status-chip ${
                                bHigh ? "high" : bMid ? "mid" : ""
                              }`}
                            >
                              {bp}%
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    <TableRow className="usage-table-footer-row">
                      <TableCell className="footer-label">Total</TableCell>

                      <TableCell className="footer-cell success">
                        {totalStats.success.toLocaleString()}
                      </TableCell>

                      <TableCell className="footer-cell error">
                        {totalStats.error.toLocaleString()}
                      </TableCell>

                      <TableCell className="footer-cell blocked">
                        {totalStats.blocked.toLocaleString()}
                      </TableCell>

                      <TableCell className="footer-cell total">
                        {totalStats.total.toLocaleString()}
                      </TableCell>

                      <TableCell className="footer-cell">
                        <Typography component="span" className="footer-pct">
                          {totalStats.blockedPct}%
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

const ViewUsageContainer = styled("div")(({ theme }) => ({
  "& button.MuiButton-root": {
    "&.Mui-disabled": {
      opacity: 0.5,
      backgroundColor: "var(--kyocera-bg-grey)",
      boxShadow:
        "0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)",
      color: "var(--kyocera-text-grey)",
    },
    marginLeft: theme.spacing(2),
  },
}));

export const ViewUsageButton: React.FC<{ onViewUsage: (app: any) => void }> = ({
  onViewUsage,
}) => {
  const { t } = useTranslation();
  const { selectedApp, applications } = useContext(ApplicationsContext);

  const selectedAppData = useMemo(() => {
    if (!selectedApp || selectedApp.split(",").length !== 1) return null;

    return applications?.devTenantApps?.find(
      (a: any) => a.clientId === selectedApp || a.id === selectedApp
    );
  }, [selectedApp, applications]);

  const handleViewUsage = () => {
    if (selectedAppData) {
      onViewUsage({
        clientId: selectedAppData.clientId,
        name: selectedAppData.name,
      });
    }
  };

  return (
    <ViewUsageContainer>
      <StyledButton
        variant="contained"
        className="d-flex align-items-center"
        startIcon={<BarChartIcon />}
        disabled={!selectedAppData}
        onClick={handleViewUsage}
        id="view-usage-button"
      >
        {t("view_usage")}
      </StyledButton>
    </ViewUsageContainer>
  );
};

export default ApplicationUsage;